import type {
  Event,
  Room,
  Timeslot,
  TimeBlock,
  Template,
  Proposal,
  Session,
  Vote,
  ScheduleItem,
  PublishedVersion,
  SyncResponse,
  Mutation,
  ApiError,
  EventFormData,
  ProposalFormData,
  RoomFormData,
  TimeslotGenerateData,
} from '../types';
import { getOrCreateDeviceId, getNickname, getAccessCode, getOrganizerToken } from './db';

const API_BASE = import.meta.env.DEV ? '/api' : '/sessionboard/api';

/**
 * Make an API request with proper headers and error handling
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  eventId?: string
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Add device ID
  const deviceId = await getOrCreateDeviceId();
  headers['X-Device-Id'] = deviceId;

  // Add nickname if available
  const nickname = await getNickname();
  if (nickname) {
    headers['X-Nickname'] = nickname;
  }

  // Add access code if available
  if (eventId) {
    const accessCode = await getAccessCode(eventId);
    if (accessCode) {
      headers['X-Access-Code'] = accessCode;
    }

    const organizerToken = await getOrganizerToken(eventId);
    if (organizerToken) {
      headers['Authorization'] = `Bearer ${organizerToken}`;
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      status: response.status,
      type: 'about:blank',
      title: 'Error',
      detail: response.statusText,
    }));
    throw error;
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// ============================================
// Events API
// ============================================

export async function fetchEvents(): Promise<Event[]> {
  const data = await apiRequest<{ events: Event[] }>('/events');
  return data.events;
}

export async function fetchEvent(id: string): Promise<Event> {
  return apiRequest<Event>(`/events/${id}`);
}

/** Fetch the currently active event (for /sessionboard pretty URL). Returns null if none. */
export async function fetchActiveEvent(): Promise<Event | null> {
  try {
    return await apiRequest<Event>('/events/active');
  } catch {
    return null;
  }
}

/** Set an event as the active event (organizer only). */
export async function setActiveEvent(eventId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/set-active`, { method: 'POST' }, eventId);
}

/** Deactivate an event (organizer only) – no event will be active afterwards. */
export async function deactivateEvent(eventId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/deactivate`, { method: 'POST' }, eventId);
}

export async function updateEvent(
  eventId: string,
  data: { name?: string; timezone?: string; startDate?: string; defaultSlotDurationMinutes?: number }
): Promise<void> {
  await apiRequest(`/events/${eventId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, eventId);
}

export async function createEvent(data: EventFormData): Promise<{ id: string }> {
  return apiRequest<{ id: string }>('/events', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function loginOrganizer(eventId: string, password: string): Promise<{ token: string }> {
  return apiRequest<{ token: string }>(`/events/${eventId}/login`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export async function verifyAccessCode(eventId: string, accessCode: string): Promise<{ valid: boolean }> {
  return apiRequest<{ valid: boolean }>(`/events/${eventId}/verify-access`, {
    method: 'POST',
    body: JSON.stringify({ accessCode }),
  });
}

export async function archiveEvent(eventId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/archive`, { method: 'POST' }, eventId);
}

// ============================================
// Rooms API
// ============================================

export async function fetchRooms(eventId: string): Promise<Room[]> {
  const data = await apiRequest<{ rooms: Room[] }>(`/events/${eventId}/rooms`, {}, eventId);
  return data.rooms;
}

export async function createRoom(eventId: string, data: RoomFormData): Promise<{ id: string }> {
  return apiRequest<{ id: string }>(`/events/${eventId}/rooms`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, eventId);
}

export async function updateRoom(eventId: string, roomId: string, data: Partial<RoomFormData & { sortOrder: number }>): Promise<void> {
  await apiRequest(`/events/${eventId}/rooms/${roomId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, eventId);
}

export async function reorderRooms(eventId: string, order: string[]): Promise<void> {
  await apiRequest(`/events/${eventId}/rooms/reorder`, {
    method: 'POST',
    body: JSON.stringify({ order }),
  }, eventId);
}

export async function deleteRoom(eventId: string, roomId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/rooms/${roomId}`, { method: 'DELETE' }, eventId);
}

// ============================================
// Timeslots API
// ============================================

export async function fetchTimeslots(eventId: string): Promise<Timeslot[]> {
  const data = await apiRequest<{ timeslots: Timeslot[] }>(`/events/${eventId}/timeslots`, {}, eventId);
  return data.timeslots;
}

export async function generateTimeslots(eventId: string, data: TimeslotGenerateData): Promise<Timeslot[]> {
  const result = await apiRequest<{ timeslots: Timeslot[] }>(
    `/events/${eventId}/timeslots/generate`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    eventId
  );
  return result.timeslots;
}

export async function deleteTimeslot(eventId: string, timeslotId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/timeslots/${timeslotId}`, { method: 'DELETE' }, eventId);
}

// ============================================
// Time Blocks API
// ============================================

export async function fetchTimeBlocks(eventId: string): Promise<TimeBlock[]> {
  const data = await apiRequest<{ timeBlocks: TimeBlock[] }>(`/events/${eventId}/time-blocks`, {}, eventId);
  return data.timeBlocks;
}

export async function createTimeBlock(
  eventId: string,
  data: { startTs: string; endTs: string; slotDurationMinutes: number; label?: string }
): Promise<{ id: string; timeslots: Array<{ id: string; indexInDay: number; start: string; end: string }> }> {
  return apiRequest(`/events/${eventId}/time-blocks`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, eventId);
}

export async function deleteTimeBlock(eventId: string, blockId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/time-blocks/${blockId}`, { method: 'DELETE' }, eventId);
}

// ============================================
// Template API
// ============================================

export async function fetchTemplate(eventId: string): Promise<Template> {
  return apiRequest<Template>(`/events/${eventId}/template`, {}, eventId);
}

export async function updateTemplate(eventId: string, template: Template): Promise<void> {
  await apiRequest(`/events/${eventId}/template`, {
    method: 'PUT',
    body: JSON.stringify(template),
  }, eventId);
}

export async function renameTemplateOption(
  eventId: string,
  field: 'tags' | 'language' | 'format' | 'level',
  oldValue: string,
  newValue: string,
): Promise<{ proposalsUpdated: number; sessionsUpdated: number; roomsUpdated: number }> {
  return apiRequest(
    `/events/${eventId}/template/rename-option`,
    {
      method: 'POST',
      body: JSON.stringify({ field, oldValue, newValue }),
    },
    eventId,
  );
}

// ============================================
// Proposals API
// ============================================

export async function fetchProposals(eventId: string): Promise<Proposal[]> {
  const data = await apiRequest<{ proposals: Proposal[] }>(`/events/${eventId}/proposals`, {}, eventId);
  return data.proposals;
}

export async function fetchProposal(eventId: string, proposalId: string): Promise<Proposal> {
  return apiRequest<Proposal>(`/events/${eventId}/proposals/${proposalId}`, {}, eventId);
}

export async function fetchProposalByDisplayId(eventId: string, displayId: number): Promise<Proposal> {
  return apiRequest<Proposal>(`/events/${eventId}/proposals/by-display-id/${displayId}`, {}, eventId);
}

export async function createProposal(
  eventId: string,
  data: ProposalFormData,
  clientMutationId?: string
): Promise<{ id: string; displayId: number }> {
  return apiRequest<{ id: string; displayId: number }>(
    `/events/${eventId}/proposals`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        preferredSlotSpan: data.preferredSlotSpan ?? undefined,
        clientMutationId,
      }),
    },
    eventId
  );
}

export async function updateProposal(
  eventId: string,
  proposalId: string,
  data: Partial<ProposalFormData>
): Promise<void> {
  await apiRequest(`/events/${eventId}/proposals/${proposalId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, eventId);
}

export async function deleteProposal(eventId: string, proposalId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/proposals/${proposalId}`, { method: 'DELETE' }, eventId);
}

export async function restoreProposal(eventId: string, proposalId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/proposals/${proposalId}/restore`, { method: 'POST' }, eventId);
}

// ============================================
// Votes API
// ============================================

export async function fetchMyVotes(eventId: string): Promise<Vote[]> {
  const data = await apiRequest<{ votes: Vote[] }>(`/events/${eventId}/votes`, {}, eventId);
  return data.votes;
}

export async function vote(
  eventId: string,
  targetType: 'proposal' | 'session',
  targetId: string,
  weight: number = 1,
  nickname?: string
): Promise<{ voteCount: number }> {
  const deviceId = await getOrCreateDeviceId();
  return apiRequest<{ success: boolean; voteCount: number }>(
    `/events/${eventId}/votes`,
    {
      method: 'POST',
      body: JSON.stringify({
        targetType,
        targetId,
        weight,
        deviceId,
        nickname: nickname ?? await getNickname() ?? 'Anonymous',
      }),
    },
    eventId
  );
}

// ============================================
// Sessions API
// ============================================

export async function fetchSessions(eventId: string): Promise<Session[]> {
  const data = await apiRequest<{ sessions: Session[] }>(`/events/${eventId}/sessions`, {}, eventId);
  return data.sessions;
}

export async function createSession(
  eventId: string,
  data: {
    title: string;
    description?: string;
    fields?: Record<string, string>;
    proposalIds: string[];
    people?: Array<{ nickname: string; role: string }>;
    /** Fixed session length in slots (from proposal when created from one). */
    spanSlots?: number | null;
  }
): Promise<{ id: string }> {
  return apiRequest<{ id: string }>(`/events/${eventId}/sessions`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, eventId);
}

export async function updateSession(
  eventId: string,
  sessionId: string,
  data: {
    title?: string;
    description?: string | null;
    fields?: Record<string, string> | null;
    spanSlots?: number | null;
    people?: Array<{ nickname: string; role?: string }>;
    proposalIds?: string[];
  }
): Promise<void> {
  await apiRequest(`/events/${eventId}/sessions/${sessionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, eventId);
}

export async function deleteSession(eventId: string, sessionId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/sessions/${sessionId}`, { method: 'DELETE' }, eventId);
}

// ============================================
// Schedule API
// ============================================

export async function fetchSchedule(eventId: string): Promise<ScheduleItem[]> {
  const data = await apiRequest<{ scheduleItems: ScheduleItem[] }>(`/events/${eventId}/schedule`, {}, eventId);
  return data.scheduleItems;
}

export async function updateScheduleItem(
  eventId: string,
  sessionId: string,
  roomId: string,
  timeslotIndexStart: number,
  spanSlots: number = 1
): Promise<{ id: string }> {
  return apiRequest<{ id: string }>(
    `/events/${eventId}/schedule`,
    {
      method: 'POST',
      body: JSON.stringify({ sessionId, roomId, timeslotIndexStart, spanSlots }),
    },
    eventId
  );
}

export async function deleteScheduleItem(eventId: string, sessionId: string): Promise<void> {
  await apiRequest(`/events/${eventId}/schedule`, {
    method: 'DELETE',
    body: JSON.stringify({ sessionId }),
  }, eventId);
}

export async function generateSchedule(eventId: string): Promise<{ placed: number }> {
  return apiRequest<{ success: boolean; placed: number }>(
    `/events/${eventId}/schedule/generate`,
    { method: 'POST' },
    eventId
  );
}

// ============================================
// Published Versions API
// ============================================

export async function fetchPublishedVersion(eventId: string): Promise<PublishedVersion | null> {
  const data = await apiRequest<{ published: PublishedVersion | null }>(`/events/${eventId}/published`, {}, eventId);
  return data.published;
}

export async function publishSchedule(eventId: string): Promise<{ id: string; versionNum: number }> {
  return apiRequest<{ id: string; versionNum: number }>(`/events/${eventId}/publish`, {
    method: 'POST',
  }, eventId);
}

// ============================================
// Voting Gate API
// ============================================

export async function toggleVoting(eventId: string, votingOpen: boolean): Promise<{ votingOpen: boolean }> {
  return apiRequest<{ success: boolean; votingOpen: boolean }>(
    `/events/${eventId}/toggle-voting`,
    {
      method: 'POST',
      body: JSON.stringify({ votingOpen }),
    },
    eventId
  );
}

/** Set (or clear) the proposal currently being presented to all participants. */
export async function presentProposal(
  eventId: string,
  proposalId: string | null
): Promise<{ presentingProposalId: string | null }> {
  return apiRequest<{ success: boolean; presentingProposalId: string | null }>(
    `/events/${eventId}/present-proposal`,
    {
      method: 'POST',
      body: JSON.stringify({ proposalId }),
    },
    eventId
  );
}

// ============================================
// Participants API
// ============================================

export interface Participant {
  deviceId: string;
  nickname: string;
  shortId: string;
}

export async function fetchParticipants(eventId: string): Promise<Participant[]> {
  return apiRequest<Participant[]>(`/events/${eventId}/participants`, {}, eventId);
}

// ============================================
// Attendees (organiser-only)
// ============================================

export interface Attendee {
  deviceId: string;
  nickname: string;
  shortId: string;
  proposalCount: number;
  voteCount: number;
  firstSeen: string;
}

export async function fetchAttendees(eventId: string): Promise<Attendee[]> {
  return apiRequest<Attendee[]>(`/events/${eventId}/attendees`, {}, eventId);
}

export interface MergeResult {
  success: boolean;
  proposalsMerged: number;
  votesMerged: number;
  sourceDeviceId: string;
  targetDeviceId: string;
}

export async function mergeAttendees(
  eventId: string,
  sourceDeviceId: string,
  targetDeviceId: string,
  targetNickname?: string,
): Promise<MergeResult> {
  return apiRequest<MergeResult>(
    `/events/${eventId}/attendees/merge`,
    {
      method: 'POST',
      body: JSON.stringify({ sourceDeviceId, targetDeviceId, targetNickname }),
    },
    eventId,
  );
}

// ============================================
// Device Link Code API
// ============================================

export async function generateLinkCode(): Promise<{ code: string }> {
  return apiRequest<{ code: string }>('/device-link-code', {
    method: 'POST',
  });
}

export async function claimDevice(code: string): Promise<{ deviceId: string; nickname: string | null }> {
  return apiRequest<{ deviceId: string; nickname: string | null }>('/claim-device', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

// ============================================
// Sync API
// ============================================

export async function sync(
  eventId: string,
  sinceSeq: number,
  mutations: Mutation[]
): Promise<SyncResponse> {
  const deviceId = await getOrCreateDeviceId();
  const accessCode = await getAccessCode(eventId);
  
  return apiRequest<SyncResponse>(
    `/events/${eventId}/sync`,
    {
      method: 'POST',
      body: JSON.stringify({
        sinceSeq,
        mutations,
        deviceId,
        accessCode,
      }),
    },
    eventId
  );
}
